import type { HubCrossLink } from '../../types/hub-cross-link';
import { LOCALIZED_PATHS } from '../constants/url-constants';
import { buildRolesIndexPath } from '../role/role-hub-config';
import { salaryHubUrlPath } from '../routing/salary-url-path';
import { buildLocationSrpPath } from '../location/build-location-srp-path';
import { employersCitySlugForCity } from '../best-companies/employers-city-slugs';
import { getCityInessiveOrNull } from './generate-srp-seo-meta';
import { buildWorkModelHubPath } from '../work-model/build-work-model-hub-path';

type Locale = 'en' | 'fi' | 'sv';
export type HubIndexKind = 'employers' | 'roles' | 'salary' | 'locations' | 'remote' | 'hybrid';

// In-code maps rather than defineMessages: these links render inside the server-only SRP shell
// (RoleHeader, MarketRail) where no IntlProvider exists, same as RoleHeader's own strings.
const T: Record<Locale, {
  heading: string;
  employers: string; employersHint: string;
  roles: string; rolesHint: string;
  salary: string; salaryHint: string;
  locations: string; locationsHint: string;
  remote: string; remoteHint: string;
  hybrid: string; hybridHint: string;
  employersInCity: (city: string) => string;
  cityJobs: (city: string) => string;
}> = {
  fi: {
    heading: 'Selaa lisää',
    employers: 'Kaikki työnantajat', employersHint: 'Rekrytoivat työnantajat ja työnantajaprofiilit',
    roles: 'Kaikki ammatit', rolesHint: 'Avoimet työpaikat ammateittain',
    salary: 'Palkat ammateittain', salaryHint: 'Palkkatiedot suoraan työpaikkailmoituksista',
    locations: 'Työpaikat kaupungeittain', locationsHint: 'Avoimet paikat Suomen kaupungeissa',
    remote: 'Etätyöpaikat', remoteHint: 'Kokonaan etänä tehtävät työt',
    hybrid: 'Hybridityöpaikat', hybridHint: 'Osin etänä, osin toimistolla',
    employersInCity: (city) => `Työnantajat ${city}`,
    cityJobs: (city) => `Kaikki työpaikat ${city}`,
  },
  sv: {
    heading: 'Utforska mer',
    employers: 'Alla arbetsgivare', employersHint: 'Arbetsgivare som rekryterar och deras profiler',
    roles: 'Alla yrken', rolesHint: 'Lediga jobb per yrke',
    salary: 'Löner per yrke', salaryHint: 'Lönedata direkt från platsannonserna',
    locations: 'Jobb per stad', locationsHint: 'Lediga jobb i Finlands städer',
    remote: 'Distansjobb', remoteHint: 'Helt distansbaserade jobb',
    hybrid: 'Hybridjobb', hybridHint: 'Delvis på distans, delvis på kontoret',
    employersInCity: (city) => `Arbetsgivare i ${city}`,
    cityJobs: (city) => `Alla lediga jobb i ${city}`,
  },
  en: {
    heading: 'Explore more',
    employers: 'All employers', employersHint: 'Employers hiring now and their profiles',
    roles: 'All roles', rolesHint: 'Open jobs by role',
    salary: 'Salaries by role', salaryHint: 'Pay data straight from the listings',
    locations: 'Jobs by city', locationsHint: 'Open positions across Finnish cities',
    remote: 'Remote jobs', remoteHint: 'Fully remote roles',
    hybrid: 'Hybrid jobs', hybridHint: 'Part remote, part office',
    employersInCity: (city) => `Employers in ${city}`,
    cityJobs: (city) => `All jobs in ${city}`,
  },
};

export function hubLinksHeading(locale: Locale): string {
  return T[locale].heading;
}

/** Localized absolute path of one of the four index hubs. */
export function hubIndexPath(kind: HubIndexKind, locale: Locale): string {
  switch (kind) {
    case 'employers':
      return `/${locale}/${LOCALIZED_PATHS[locale].bestCompanies}`;
    case 'roles':
      return buildRolesIndexPath(locale);
    case 'salary':
      return salaryHubUrlPath(locale);
    case 'locations':
      return `/${locale}/${LOCALIZED_PATHS[locale].locations}`;
    case 'remote':
      return buildWorkModelHubPath('remote', locale);
    case 'hybrid':
      return buildWorkModelHubPath('hybrid', locale);
  }
}

/** Cross-links between the index hubs (employers, roles, salary, locations), in the given order. */
export function buildHubIndexLinks(locale: Locale, kinds: HubIndexKind[]): HubCrossLink[] {
  const t = T[locale];
  return kinds.map((kind) => ({
    kind: kind === 'employers' ? 'employers' : kind === 'salary' ? 'salary' : kind === 'locations' ? 'location' : kind === 'remote' || kind === 'hybrid' ? 'remote' : 'index',
    href: hubIndexPath(kind, locale),
    label: t[kind],
    hint: t[`${kind}Hint`],
  }));
}

/** City name as it reads inside a Finnish sentence ("Tampereella"); the name itself elsewhere. */
export function cityInCopy(cityName: string, locale: Locale): string {
  const city = cityName.split(',')[0].trim();
  return locale === 'fi' ? getCityInessiveOrNull(city) ?? city : city;
}

/** Link to the employers-in-city page, or null when the city has none. */
export function buildEmployersInCityLink(locale: Locale, cityName: string): HubCrossLink | null {
  const slug = employersCitySlugForCity(cityName);
  if (!slug) return null;
  return {
    kind: 'employers',
    href: `/${locale}/${LOCALIZED_PATHS[locale].bestCompanies}/${slug}`,
    label: T[locale].employersInCity(cityInCopy(cityName, locale)),
  };
}

/** Link to a curated city's location hub. */
export function buildCityJobsLink(locale: Locale, hubSlug: string, cityName: string): HubCrossLink {
  return {
    kind: 'location',
    href: `/${locale}${buildLocationSrpPath(hubSlug, locale)}`,
    label: T[locale].cityJobs(cityInCopy(cityName, locale)),
  };
}
